import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import stationManagement from '../../../../utils/api/location-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface RevenueReportChartProperties {
  startDate?: string;
  endDate?: string;
}

interface ChartDataProperties {
  label?: string;
  date?: string;
  hour?: number | string;
  time?: string;
  total?: number;
  totalRevenue?: number;
  revenueEarned?: number;
  revenue?: number;
  value?: number;
  day?: number | string;
  month?: number | string;
  monthName?: string;
  year?: number | string;
  week?: number | string;
  weekName?: string;
  [key: string]: any;
}

interface ChartWidth {
  type: 'percentage' | 'pixel';
  width: number;
}

/**
 * Month translation mapping dictionary.
 */
const monthMap: Record<string, string> = {
  Jan: 'jan',
  Feb: 'feb',
  Mar: 'mar',
  Apr: 'apr',
  May: 'may_month',
  Jun: 'jun',
  Jul: 'jul',
  Aug: 'aug',
  Sep: 'sep',
  Oct: 'oct',
  Nov: 'nov',
  Dec: 'dec',
};

/**
 * Short month names array for month resolution.
 */
const shortMonths: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Formats a chart label based on whether it represents an hour, day/date, week, or month.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {any} t - The translation function.
 * @param {string} [locale] - Current active locale.
 * @param {string} [startDate] - Optional start date of current range.
 * @param {string} [endDate] - Optional end date of current range.
 * @returns {string} Formatted label string for the chart axis.
 */
const formatChartLabel = (
  item: ChartDataProperties,
  t: any,
  locale: string = 'en',
  startDate?: string,
  endDate?: string,
): string => {
  // 1. Hour / Time format handling (e.g. { hour: 10, time: "10:00", date: "2026-08-31" })
  if (item.time !== undefined && item.time !== null && item.time !== '') {
    return String(item.time).trim().slice(0, 5);
  }
  if (item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    if (!Number.isNaN(hourNumber)) {
      return `${String(hourNumber).padStart(2, '0')}:00`;
    }
    return String(item.hour).trim();
  }

  // 2. Specific Date / Day handling (takes priority over standalone month)
  let parsedDate: Date | null = null;
  const rawDate = item.date;

  if (rawDate !== undefined && rawDate !== null && rawDate !== '') {
    const dateString = String(rawDate).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [yearString, monthPart, dayString] = dateString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [dayString, monthPart, yearString] = dateString.split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (dateString.includes('-') || dateString.includes('/')) {
      const generalDate = new Date(dateString);
      if (!Number.isNaN(generalDate.getTime())) {
        parsedDate = generalDate;
      }
    }
  }

  // If parsed date was not found via rawDate, check day + month numbers
  if (!parsedDate && item.day !== undefined && item.day !== null && item.day !== '') {
    const dayNumber = Number(item.day);
    const rawMonthValue = item.month ?? item.monthName;
    const monthNumber =
      rawMonthValue !== undefined && rawMonthValue !== null && rawMonthValue !== ''
        ? Number(rawMonthValue)
        : NaN;

    if (!Number.isNaN(dayNumber) && dayNumber >= 1 && dayNumber <= 31) {
      let resolvedMonth =
        !Number.isNaN(monthNumber) && monthNumber >= 1 && monthNumber <= 12 ? monthNumber : NaN;

      if (Number.isNaN(resolvedMonth) && typeof rawMonthValue === 'string') {
        const monthIndex = shortMonths.findIndex(
          (m) => m.toLowerCase() === rawMonthValue.trim().slice(0, 3).toLowerCase(),
        );
        if (monthIndex !== -1) {
          resolvedMonth = monthIndex + 1;
        }
      }

      if (!Number.isNaN(resolvedMonth)) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, resolvedMonth - 1, dayNumber);
        if (!Number.isNaN(dateObject.getTime())) {
          parsedDate = dateObject;
        }
      }
    }
  }

  if (parsedDate) {
    const isMultiYear =
      Boolean(startDate) &&
      Boolean(endDate) &&
      new Date(startDate!).getFullYear() !== new Date(endDate!).getFullYear();

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      ...(isMultiYear ? { year: '2-digit' } : {}),
    }).format(parsedDate);
  }

  // 3. Week format handling
  const weekValue = item.weekName || item.week;
  if (weekValue !== undefined && weekValue !== null && weekValue !== '') {
    const weekString = String(weekValue).trim();
    const weekMatch = weekString.match(/^week\s*(\d+)$/i) || weekString.match(/^w\s*(\d+)$/i);
    if (weekMatch) {
      return `${t('week', { defaultValue: 'Week' })} ${weekMatch[1]}`;
    }
    if (/^\d+$/.test(weekString)) {
      return `${t('week', { defaultValue: 'Week' })} ${weekString}`;
    }
    return weekString;
  }

  // 4. Month format handling (monthly aggregate without day)
  const rawMonth = item.monthName || item.month;
  if (rawMonth !== undefined && rawMonth !== null && rawMonth !== '') {
    const monthString = String(rawMonth).trim();

    // Check numeric month 1-12 or "01"-"12"
    if (/^\d{1,2}$/.test(monthString)) {
      const monthNumber = Number(monthString);
      if (monthNumber >= 1 && monthNumber <= 12) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, monthNumber - 1, 1);
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return item.year ? `${monthShort} ${item.year}` : monthShort;
      }
    }

    // Check "Jan 2024" or "Jan 24" format
    const parts = monthString.split(' ');
    if (parts.length === 2 && monthMap[parts[0]]) {
      return `${t(monthMap[parts[0]])} ${parts[1]}`;
    }

    // Check "Jan", "Feb" format
    if (monthMap[monthString]) {
      return t(monthMap[monthString]);
    }

    // Check "YYYY-MM" format e.g. "2026-08"
    if (/^\d{4}-\d{2}$/.test(monthString)) {
      const [year, month] = monthString.split('-').map(Number);
      const dateObject = new Date(year, month - 1, 1);
      if (!Number.isNaN(dateObject.getTime())) {
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return `${monthShort} ${year}`;
      }
    }

    return monthString;
  }

  // 5. Fallback on label
  if (item.label !== undefined && item.label !== null && item.label !== '') {
    const labelString = String(item.label).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(labelString)) {
      const [yearString, monthPart, dayString] = labelString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        return new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: 'short',
        }).format(dateObject);
      }
    }
    if (/^\d{1,2}$/.test(labelString)) {
      const numericMonth = Number(labelString);
      if (numericMonth >= 1 && numericMonth <= 12) {
        const dateObject = new Date(2000, numericMonth - 1, 1);
        return new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
      }
    }
    if (monthMap[labelString]) {
      return t(monthMap[labelString]);
    }
    return labelString;
  }

  return '';
};

/**
 * Formats a detailed title string for the chart tooltip.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {any} t - The translation function.
 * @param {string} [locale] - Current active locale.
 * @returns {string} Formatted tooltip title string.
 */
const getTooltipTitle = (
  item: ChartDataProperties,
  t: any,
  locale: string = 'en',
): string => {
  // 1. Time / Hour with date
  if (item.date && item.time) {
    return `${item.date} ${item.time}`;
  }
  if (item.date && item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    const hourPad = !Number.isNaN(hourNumber)
      ? `${String(hourNumber).padStart(2, '0')}:00`
      : String(item.hour);
    return `${item.date} ${hourPad}`;
  }

  // 2. Specific date
  if (item.date && /^\d{4}-\d{2}-\d{2}/.test(String(item.date))) {
    const [yearString, monthPart, dayString] = String(item.date).split('T')[0].split('-');
    const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
    if (!Number.isNaN(dateObject.getTime())) {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  // 3. Day + Month
  if (item.day !== undefined && item.day !== '' && item.month !== undefined && item.month !== '') {
    const d = Number(item.day);
    const m = Number(item.month);
    const y = Number(item.year) || new Date().getFullYear();
    if (!Number.isNaN(d) && !Number.isNaN(m) && m >= 1 && m <= 12) {
      const dateObject = new Date(y, m - 1, d);
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  // 4. Month with Year
  if (item.month && item.year) {
    const m = Number(item.month);
    const y = Number(item.year);
    if (!Number.isNaN(m) && !Number.isNaN(y) && m >= 1 && m <= 12) {
      const dateObject = new Date(y, m - 1, 1);
      return new Intl.DateTimeFormat(locale, {
        month: 'long',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  return item.label || '';
};

/**
 * Revenue Report Chart
 * @param {RevenueReportChartProperties} properties - Component properties
 * @param {string} [properties.startDate] - Start date
 * @param {string} [properties.endDate] - End date
 * @returns {React.JSX.Element} The rendered component
 */
function RevenueReportChart({ startDate = '', endDate = '' }: RevenueReportChartProperties) {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartWidth, setChartWidth] = useState<ChartWidth>({
    type: 'percentage',
    width: 100,
  });

  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();
  const tooltipBgColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-tooltip-bg-color')
    .trim();
  const tooltipTextColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-tooltip-text-color')
    .trim();

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: tooltipBgColor,
        titleColor: tooltipTextColor,
        bodyColor: tooltipTextColor,
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          /**
           * Returns the tooltip title.
           * @param {any} context - The tooltip context object.
           * @returns {string} The tooltip title string.
           */
          title: (context: any) => {
            const index = context[0]?.dataIndex;
            const item = barGraphData[index];
            if (item) {
              const formattedTitle = getTooltipTitle(item, t, i18n.language);
              if (formattedTitle) {
                return formattedTitle;
              }
            }
            return context[0]?.label || `${t('revenue')} (${t('sar')})`;
          },
          /**
           * Formats the tooltip label using locale string.
           * @param {any} context - The tooltip context object.
           * @returns {string} The formatted label string.
           */
          label: (context: any) => `${Number(context.raw ?? 0).toLocaleString()} ${t('sar')}`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 12,
          maxRotation: 45,
          minRotation: 0,
          font: { size: 12 },
        },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  /** Draws the vertical crosshair line using --success theme color on hover */
  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a colored vertical crosshair line at the hovered x position.
     * @param {any} chart - The Chart.js instance
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successColor;
        context.stroke();
        context.restore();
      }
    },
  };

  /**
   * Fetches revenue report data based on date range.
   * @param {string} [fetchStartDate] - The start date.
   * @param {string} [fetchEndDate] - The end date.
   * @returns {Promise<void>} Promise resolving when data is fetched.
   */
  const fetchRevenueReport = async (fetchStartDate?: string, fetchEndDate?: string) => {
    setLoading(true);

    try {
      const response = await stationManagement.fetchRevenueChart({
        startDate: fetchStartDate,
        endDate: fetchEndDate,
        type: 'revenue',
        chargingStation: selectedChargingPoint?.id,
      });

      if (response.status === 200) {
        const rawList = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        // Normalize raw items: extract Mongo _id subfields (day, month, year) if present
        const normalizedList = rawList.map((item: any) => {
          const idObject = item._id && typeof item._id === 'object' ? item._id : {};
          const day = item.day ?? idObject.day;
          const month = item.month ?? item.monthName ?? idObject.month;
          const year = item.year ?? idObject.year;

          let derivedDate = item.date;
          if (!derivedDate && day && month) {
            const y =
              year ||
              (fetchStartDate ? new Date(fetchStartDate).getFullYear() : new Date().getFullYear());
            const m = String(month).padStart(2, '0');
            const d = String(day).padStart(2, '0');
            derivedDate = `${y}-${m}-${d}`;
          }

          return {
            ...item,
            day,
            month,
            year,
            date: derivedDate || item.date || item.createdAt || '',
          };
        });

        // Sort items chronologically
        normalizedList.sort((a: any, b: any) => {
          const dateA = a.date ? new Date(a.date).getTime() : NaN;
          const dateB = b.date ? new Date(b.date).getTime() : NaN;
          if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && dateA !== dateB) {
            return dateA - dateB;
          }
          if (a.year !== undefined && b.year !== undefined && a.year !== b.year) {
            return Number(a.year) - Number(b.year);
          }
          if (a.month !== undefined && b.month !== undefined && a.month !== b.month) {
            return Number(a.month) - Number(b.month);
          }
          if (a.day !== undefined && b.day !== undefined && a.day !== b.day) {
            return Number(a.day) - Number(b.day);
          }
          if (a.hour !== undefined && b.hour !== undefined && a.hour !== b.hour) {
            return Number(a.hour) - Number(b.hour);
          }
          return 0;
        });

        setBarGraphData(normalizedList);
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueReport(startDate, endDate);
  }, [startDate, endDate, selectedChargingPoint?.id]);

  useEffect(() => {
    if (barGraphData.length <= 10) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 50,
      });
    }
  }, [barGraphData]);

  const labels = barGraphData.map((d: any) =>
    formatChartLabel(d, t, i18n.language, startDate, endDate),
  );
  const values = barGraphData.map((d: any) =>
    Number(d.total ?? d.totalRevenue ?? d.revenueEarned ?? d.revenue ?? d.value ?? 0),
  );

  return (
    <div className="report-revenue-chart-wrap">
      <div className="report-revenue-container">
        <div className="report-revenue-header">
          <div className="report-revenue-title"> {t('revenue')}</div>
        </div>
        <div className="report-revenue-chart-container">
          <div className="report-revenue-chart">
            {loading ? (
              <div className="loader">
                <BatteryLoader />
              </div>
            ) : (
              <ResponsiveContainer
                width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
                height={250}>
                <Line
                  data={{
                    labels,
                    datasets: [
                      {
                        label: t('revenue'),
                        data: values,
                        borderColor: primaryColor,
                        backgroundColor: primaryColor,
                        fill: false,
                        tension: 0.4,
                        borderWidth: 2,
                        pointRadius: barGraphData.length > 25 ? 0 : 3,
                        pointHoverRadius: 6,
                        pointBackgroundColor: primaryColor,
                        pointBorderColor: primaryColor,
                      },
                    ],
                  }}
                  options={chartOptions}
                  plugins={[verticalHoverLinePlugin]}
                />
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

RevenueReportChart.defaultProps = {
  startDate: '',
  endDate: '',
};

export default RevenueReportChart;